/**
 * AI Companion Section Component
 * Displays a card that opens the AI Assistant panel for asking questions about BwC data
 *
 * Features:
 * - Quick insight scenario buttons
 * - Opens chat panel for interactive Q&A
 * - Uses OpenAI to analyze customer data
 * - Persists conversation history across panel open/close
 */

import { Icon, TooltipHost, DirectionalHint } from "@fluentui/react";
import React, { useState } from "react";
import { IAICompanionSectionProps, IBwCAIChatMessage } from "./BuildWithCustomer.interfaces";
import BwCAIAssistantPanel from "./BwCAIAssistantPanel";

// Inline styles
const STYLES = {
    container: {
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        borderRadius: "16px",
        padding: "20px 24px",
        marginBottom: "20px",
        boxShadow: "0 8px 32px rgba(102, 126, 234, 0.35)",
        position: "relative" as const,
        overflow: "hidden" as const
    },
    content: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "24px"
    },
    leftSection: {
        display: "flex",
        alignItems: "center",
        gap: "16px",
        flex: 1
    },
    iconContainer: {
        width: "56px",
        height: "56px",
        borderRadius: "14px",
        background: "rgba(255, 255, 255, 0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "26px",
        color: "#fff",
        flexShrink: 0
    },
    textSection: {
        color: "#fff"
    },
    title: {
        fontSize: "18px",
        fontWeight: 700,
        marginBottom: "4px",
        display: "flex",
        alignItems: "center",
        gap: "8px"
    },
    subtitle: {
        fontSize: "13px",
        opacity: 0.9,
        lineHeight: 1.4
    },
    actionButton: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "14px 24px",
        background: "rgba(255, 255, 255, 0.95)",
        color: "#667eea",
        borderRadius: "12px",
        fontSize: "14px",
        fontWeight: 600,
        cursor: "pointer",
        border: "none",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
        flexShrink: 0
    },
    actionButtonHover: {
        transform: "translateY(-2px)",
        boxShadow: "0 6px 20px rgba(0, 0, 0, 0.2)"
    },
    scenariosContainer: {
        display: "flex",
        gap: "10px",
        marginTop: "16px",
        flexWrap: "wrap" as const
    },
    scenarioChip: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "8px 14px",
        background: "rgba(255, 255, 255, 0.15)",
        border: "1px solid rgba(255, 255, 255, 0.3)",
        borderRadius: "20px",
        color: "#fff",
        fontSize: "12px",
        cursor: "pointer",
        transition: "all 0.2s ease"
    },
    scenarioChipHover: {
        background: "rgba(255, 255, 255, 0.25)",
        borderColor: "rgba(255, 255, 255, 0.5)"
    }
};

// Quick insight scenarios to display as chips
const QUICK_SCENARIOS = [
    {
        id: "overall-bwc-insights",
        icon: "Lightbulb",
        label: "Overall BwC Insights",
        prompt: `Build With Customer (BwC) Feature Insights

Objective:
Create an executive-ready summary of feature testing currently in progress across Build With Customer (BwC) engagements, using an Eval AI / startup-style assessment.

Instructions:
Using notes, customer feedback, and engagement summaries, produce the following:

Top-Level Summary (at the top)
- Total number of customers currently testing
- Total number of features under testing
- High-level signal:
  - How many features are going well
  - How many features are facing friction
  - Any features at risk / needing follow-up

Detailed Breakdown by BwC
For each Build With Customer (BwC), summarize in the format below:
- Feature Name:
- Customer(s):
- For each feature under this BwC:
  - Testing Status:  Going well |  Some friction |  Significant friction / blocked
  - What's Working Well: Key positive outcomes or validation from customers
  - Friction / Challenges: Gaps, usability issues, missing capabilities, performance concerns, or integration pain points
  - Key Customer Feedback (themes): Direct signals or summarized sentiment from notes
  - Current Outcome / Signal: Clear recommendation such as: Proceed / scale, Iterate and re-test, Needs product decision, Pause or deprioritize

Cross-Cutting Insights (Optional but Recommended)
- Common patterns across customers
- Repeated friction points across multiple features
- Any early indicators of product-market fit
- Dependencies or asks for engineering, UX, or GTM

Tone & Style Guidelines:
- Concise, startup-style evaluation
- Outcome-oriented, not descriptive
- Use bullets, not paragraphs
- Call out signals and decisions, not raw notes

Output Format:
- Start with Executive Summary
- Follow with BwC-by-BwC breakdown
- End with Key Takeaways / Next Steps`
    },
    {
        id: "weekly-bwc-insights",
        icon: "Calendar",
        label: "This week's BwC Insights",
        prompt: `This Week's BwC Activity Summary

Objective:
Create a focused summary of BwC activity from the PAST 7 DAYS ONLY, highlighting what changed this week.

Instructions:
Focus ONLY on recent activity by analyzing:
1. Look at 'lastModifiedNotesDate' - only include customers with notes updated in the past 7 days
2. Look at 'createdOn' - identify any NEW customers added this week
3. Reference the 'Customers Added in the Past Week' data section

Report Structure:

## New Customers This Week
- List all customers added in the past week (from the 'recentCustomers' data)
- For each: Customer Name, Program/Squad, Status, CxE Lead, Region

## Recent Notes Activity (Past 7 Days)
- Which customers had notes updated recently?
- What are the key themes from recent notes?
- Any blockers or issues mentioned in recent notes?

## Weekly Status Changes
- Any customers who changed status this week?
- New "In Progress" customers that started testing?

## Key Highlights
- What's the most important signal from this week's data?
- Any urgent follow-ups needed?

Tone:
- Brief and action-oriented
- Focus on CHANGES and NEW information, not the overall state
- Highlight what's different from last week`
    },
    {
        id: "engagements-hygiene",
        icon: "Health",
        label: "Engagements Hygiene",
        prompt: `Engagements Hygiene Report

Objective:
Identify in-progress customer engagements where notes are missing, grouped by Feature Name, to improve CRM hygiene and ensure effective customer engagement tracking.

Instructions:
Please generate a report for all In Progress engagements where engagement notes are not present or are empty.

For each record, organize the output by Feature Name, and include the following details:

For Each Feature:
- Feature Name
- For each customer under that feature:
  - Customer Name (hyperlinked with respective CRM Program Participant link)
  - CxE Lead (Name + Alias)
  - Program Name
  - Engagement Status (In Progress)
  - Last Activity Date (if available)
  - Notes Status: Missing / Not Updated

Top-Level Summary (Required):
At the top of the output, include:
- Total number of In-Progress engagements
- Number of engagements missing notes
- Number of unique features impacted
- Percentage of In-Progress engagements with missing notes

Purpose / Outcome:
This report will be used to:
- Track CRM hygiene gaps
- Improve customer engagement quality
- Drive follow-ups with CxE Leads and Program owners
- Increase visibility into feature-level customer activity

Output Format:
- Start with the Executive Summary
- Follow with a Feature-by-Feature breakdown
- Use tables or bullet points
- Keep the tone concise, factual, and action-oriented

Optional Enhancements (if data is available):
- Flag engagements missing notes for >14 days / >30 days
- Add a "Next Action Required" column
- Highlight features with multiple customers missing notes`
    },
    { id: "engagement", icon: "AnalyticsView", label: "Deep Engagement", prompt: "From the notes data: Which customers are deeply engaged for which features in the past 1 week? List scenarios, feedback, and gaps. What features are they requesting along with feature evidences?" },
    { id: "summary", icon: "BarChartVertical", label: "Status Summary", prompt: "Give me a summary of customers by status. How many are Nominated, Pending, Committed, In Progress, Completed, and Declined?" },
    { id: "squads", icon: "Org", label: "Squad Overview", prompt: "Give me an overview of all squads. Which squads have the most customers? Which squads have customers in active testing (In Progress)?" }
];

/**
 * Scenario Chip Component
 */
const ScenarioChip: React.FC<{
    icon: string;
    label: string;
    onClick: () => void;
}> = ({ icon, label, onClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <button
            style={{
                ...STYLES.scenarioChip,
                ...(isHovered ? STYLES.scenarioChipHover : {})
            }}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Icon iconName={icon} style={{ fontSize: 12 }} />
            {label}
        </button>
    );
};

/**
 * Main AI Companion Section Component
 */
export const AICompanionSection: React.FC<IAICompanionSectionProps> = ({ customers, isLoading }) => {
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [isButtonHovered, setIsButtonHovered] = useState(false);
    const [initialPrompt, setInitialPrompt] = useState<string | undefined>(undefined);

    // Persist conversation state across panel open/close
    const [messages, setMessages] = useState<IBwCAIChatMessage[]>([]);
    const [sessionId, setSessionId] = useState<string>(`bwc-ai-${Date.now()}`);

    const handleOpenPanel = () => {
        setInitialPrompt(undefined); // No initial prompt when opening via button
        setIsPanelOpen(true);
    };

    const handleScenarioClick = (prompt: string) => {
        // Keep previous conversation and add new insight to it
        setInitialPrompt(prompt);
        setIsPanelOpen(true);
    };

    const handleClosePanel = () => {
        setIsPanelOpen(false);
        setInitialPrompt(undefined);
    };

    const handleInitialPromptSent = () => {
        setInitialPrompt(undefined);
    };

    return (
        <>
            <div style={STYLES.container}>
                <div style={STYLES.content}>
                    {/* Left Section - Icon and Text */}
                    <div style={STYLES.leftSection}>
                        <div style={STYLES.iconContainer}>
                            <Icon iconName="Robot" />
                        </div>
                        <div style={STYLES.textSection}>
                            <div style={STYLES.title}>
                                AI Companion
                                <TooltipHost
                                    content="Ask questions about your BwC data using AI"
                                    directionalHint={DirectionalHint.topCenter}
                                >
                                    <Icon
                                        iconName="Info"
                                        style={{ fontSize: 14, opacity: 0.7, cursor: "help" }}
                                    />
                                </TooltipHost>
                            </div>
                            <div style={STYLES.subtitle}>
                                Ask questions about your Build with Customer data.
                                Get insights on testing status, engagement, feature requests, and more.
                            </div>
                        </div>
                    </div>

                    {/* Action Button */}
                    <button
                        style={{
                            ...STYLES.actionButton,
                            ...(isButtonHovered ? STYLES.actionButtonHover : {})
                        }}
                        onClick={handleOpenPanel}
                        onMouseEnter={() => setIsButtonHovered(true)}
                        onMouseLeave={() => setIsButtonHovered(false)}
                    >
                        <Icon iconName="ChatBot" style={{ fontSize: 18 }} />
                        Ask AI Assistant
                    </button>
                </div>

                {/* Quick Scenario Chips */}
                <div style={STYLES.scenariosContainer}>
                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginRight: 4 }}>
                        Quick insights:
                    </span>
                    {QUICK_SCENARIOS.map(scenario => (
                        <ScenarioChip
                            key={scenario.id}
                            icon={scenario.icon}
                            label={scenario.label}
                            onClick={() => handleScenarioClick(scenario.prompt)}
                        />
                    ))}
                </div>
            </div>

            {/* AI Assistant Panel */}
            <BwCAIAssistantPanel
                isOpen={isPanelOpen}
                onDismiss={handleClosePanel}
                customers={customers}
                isLoadingData={isLoading}
                initialPrompt={initialPrompt}
                onInitialPromptSent={handleInitialPromptSent}
                messages={messages}
                onMessagesChange={setMessages}
                sessionId={sessionId}
                onSessionIdChange={setSessionId}
            />
        </>
    );
};

export default AICompanionSection;
